import { useCallback, useEffect, useRef } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";

export interface StableWebSocketOptions {
  onOpen?: (event: WebSocketEventMap["open"]) => void;
  onClose?: (event: WebSocketEventMap["close"]) => void;
  onError?: (event: WebSocketEventMap["error"]) => void;
  onMessage?: (event: WebSocketEventMap["message"]) => void;
  onReconnectStop?: (numAttempts: number) => void;
  // Custom options for deployment handling
  deploymentGracePeriod?: number; // ms to wait for server restart
  maxReconnectAttempts?: number;
}

export type ConnectionStatus =
  | "connecting"
  | "connected"
  | "disconnected"
  | "reconnecting"
  | "server_restarting"
  | "error";

/**
 * A stable WebSocket hook that prevents thrashing and handles deployments gracefully.
 *
 * Key features:
 * - Prevents infinite reconnection loops
 * - Handles HMR gracefully without component remounting
 * - Detects deployment scenarios and provides appropriate UX
 * - Uses exponential backoff with deployment-aware delays
 * - Shares connections to prevent multiple sockets
 */
export function useStableWebSocket(
  url: string | (() => Promise<string>) | null,
  options: StableWebSocketOptions = {},
) {
  const {
    onOpen,
    onClose,
    onError,
    onMessage,
    onReconnectStop,
    deploymentGracePeriod = 5000,
    maxReconnectAttempts = 10,
  } = options;

  // Track component lifecycle
  const didUnmount = useRef(false);
  const deploymentDetected = useRef(false);
  const lastCloseTime = useRef<number>(0);
  const consecutiveClosures = useRef(0);

  // Detect deployment pattern: multiple rapid closures with code 1006
  const detectDeployment = useCallback((closeEvent: CloseEvent) => {
    const now = Date.now();
    const timeSinceLastClose = now - lastCloseTime.current;

    // Code 1006 = abnormal closure (server restart/deployment)
    if (closeEvent.code === 1006) {
      if (timeSinceLastClose < 10000) {
        // Within 10 seconds
        consecutiveClosures.current += 1;
      } else {
        consecutiveClosures.current = 1;
      }

      // If multiple rapid 1006 closures, likely a deployment
      if (consecutiveClosures.current >= 2) {
        deploymentDetected.current = true;
        console.log(
          "🚀 Deployment detected - will reconnect with longer delay",
        );
      }
    } else {
      consecutiveClosures.current = 0;
      deploymentDetected.current = false;
    }

    lastCloseTime.current = now;
  }, []);

  const { sendMessage, lastMessage, readyState, getWebSocket } = useWebSocket(
    url,
    {
      shouldReconnect: (closeEvent) => {
        // Don't reconnect if component unmounted
        if (didUnmount.current) {
          console.log("⏹️  Component unmounted - skipping reconnection");
          return false;
        }

        // Detect deployment patterns
        detectDeployment(closeEvent);

        return true;
      },

      reconnectAttempts: maxReconnectAttempts,

      // Smart reconnection interval with deployment awareness
      reconnectInterval: (attemptNumber) => {
        const baseDelay = 2 ** attemptNumber * 1000;

        if (deploymentDetected.current) {
          // Longer delays for server restarts
          const deploymentDelay = Math.max(
            deploymentGracePeriod,
            baseDelay * 2,
          );
          console.log(
            `⏳ Deployment reconnect attempt ${attemptNumber + 1} in ${deploymentDelay}ms`,
          );
          return Math.min(deploymentDelay, 30000); // Cap at 30s for deployments
        } else {
          // Normal exponential backoff
          return Math.min(baseDelay, 10000); // Cap at 10s for normal issues
        }
      },

      // Critical: Share connections to prevent multiple sockets
      share: true,

      // Heartbeat to detect stale connections
      heartbeat: {
        message: "ping",
        returnMessage: "pong",
        timeout: 60000, // 1 minute timeout
        interval: 25000, // Ping every 25 seconds
      },

      // Enhanced event handlers
      onOpen: (event) => {
        const isReconnection = lastCloseTime.current > 0;
        if (isReconnection) {
          if (deploymentDetected.current) {
            console.log("🎉 Reconnected after deployment");
            deploymentDetected.current = false;
          } else {
            console.log("🔄 Reconnected successfully");
          }
        } else {
          console.log("🔌 Connected to WebSocket");
        }

        // Reset deployment detection on successful connection
        consecutiveClosures.current = 0;

        onOpen?.(event);
      },

      onClose: (event) => {
        if (!didUnmount.current) {
          if (deploymentDetected.current) {
            console.log(
              "🚀 Connection closed during deployment - will reconnect...",
            );
          } else {
            console.log(`🔌 Connection closed (code: ${event.code})`);
          }
        }

        onClose?.(event);
      },

      onError: (event) => {
        console.error("❌ WebSocket error:", event);
        onError?.(event);
      },

      onMessage,
      onReconnectStop,
    },
  );

  // Enhanced connection status with deployment awareness
  const connectionStatus: ConnectionStatus = (() => {
    switch (readyState) {
      case ReadyState.CONNECTING:
        return deploymentDetected.current ? "server_restarting" : "connecting";
      case ReadyState.OPEN:
        return "connected";
      case ReadyState.CLOSING:
        return "disconnected";
      case ReadyState.CLOSED:
        return deploymentDetected.current
          ? "server_restarting"
          : "disconnected";
      default:
        return "error";
    }
  })();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      didUnmount.current = true;
      console.log("🧹 WebSocket hook cleanup");
    };
  }, []);

  // Enhanced sendMessage with queuing info
  const enhancedSendMessage = useCallback(
    (message: string, keep = true) => {
      if (readyState === ReadyState.OPEN) {
        sendMessage(message, keep);
      } else {
        if (keep) {
          console.log(
            "📤 Message queued (connection not ready):",
            message.slice(0, 50),
          );
          sendMessage(message, keep);
        } else {
          console.warn("📤 Message dropped (connection not ready, keep=false)");
        }
      }
    },
    [sendMessage, readyState],
  );

  return {
    sendMessage: enhancedSendMessage,
    lastMessage,
    readyState,
    connectionStatus,
    getWebSocket,
    isConnected: readyState === ReadyState.OPEN,
    isConnecting: readyState === ReadyState.CONNECTING,
    isDeploymentReconnecting:
      deploymentDetected.current && readyState !== ReadyState.OPEN,
  };
}
